import { bulkType, pageViewList } from "@assets/constant/index.constant";
import { ChevronsIcon, DragBar, Equal, GridBar, RowBar } from "@assets/icons";
import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useInfiniteScroller } from "@pages/dashboard/test/hook";
import { endpoint as apiEndpoint, getOptions } from "@services/api.services";
import { baseURL } from "@services/index.services";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonOne } from "@ui/external/components/skeleton";
import { showToast } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { useSelectionStore } from "@utils/states/index.state";
import {
  ArrowDownWideNarrow,
  ArrowUpDown,
  ArrowUpDownIcon,
  ArrowUpNarrowWide,
  ChevronLeft,
  EllipsisVertical,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { useInView } from "react-intersection-observer";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import "./index.css";
import { cn } from "@/lib/utils";
import SortingForm from "../sorting-form";

type Props = {
  content: { title: string; description?: string };
  children?: React.ReactElement;
  isGroupAction?: boolean;
  isOrientation?: boolean;
  formId?: string;
  filter?: React.ReactNode;
  inject_ui_table_actions?: React.ReactNode;
  /* extra controls rendered next to Clear/Delete while rows are selected */
  inject_bulk_actions?: React.ReactNode;
  bulkActions?: boolean;
  hideSort?: boolean;
  count?: number;
  className?: string;
  data: any[];
  [key: string]: any;
};
const TableHeader: React.FC<Props> = ({
  content,
  children,
  count = 0,
  grid,
  bulkActions = false,
  hideSort = false,
  isGroupAction = false,
  formId,
  isOrientation = false,
  inject_ui_table_actions,
  inject_bulk_actions,
  filter,
  className,
  data,
  ...props
}) => {
  const { title = "Unnamed Table", description } = content || {};
  const { searchParams, setSearchParams, handleAction } = useHandleAction({});

  /* =================== page meta data =================== */
  const { type } = useParams();
  const pathname = usePathname();
  const pageMode = searchParams.get("page");
  const modalMode = searchParams.get("modal");
  const orientationMode = searchParams.get("orientation");
  const openTypes = ["add", "edit"];

  const handleGroup = (e: "row" | "grid" | "drag") => {
    searchParams.set("orientation", e);
    setSearchParams(() => searchParams);
  };
  const getOrientation = (): "row" | "grid" | "drag" => {
    const value = orientationMode;
    return value === "row" || value === "grid" || value === "drag"
      ? value
      : "row";
  };

  const {
    selectedRows,
    setSelectedRows,
    isFormPending,
    setSelectedRowDetails,
  } = useSelectionStore();

  const navigate = useNavigate();
  useEffect(() => {
    if (openTypes.includes(pageMode || modalMode || "")) {
      setSelectedRows([]);
    }
  }, [searchParams]);
  const handleCheck = (type: "single" | "all", id?: string | number) => {
    let selection: number[] = [];
    if (type === "all") {
      const currentPageIds = data.map((item) => item.id);
      const allSelectedForPage = currentPageIds.every((pid) =>
        selectedRows.includes(pid),
      );

      selection = allSelectedForPage
        ? // Deselect only the current page's items
          selectedRows.filter((id) => !currentPageIds.includes(id))
        : // Add only missing current page's items
          [
            ...selectedRows,
            ...currentPageIds.filter((id) => !selectedRows.includes(id)),
          ];

      setSelectedRows(selection);
      setSelectedRowDetails(
        selection.length
          ? data.filter((item) => selection.includes(item.id))
          : [],
      );
      return;
    }

    if (typeof id === "undefined") return;
    selection =
      id && typeof id === "number" && selectedRows.includes(id)
        ? (selectedRows.filter((i) => i !== id) as number[])
        : ([...selectedRows, id] as number[]);
    setSelectedRows(selection as any[]);
    setSelectedRowDetails(
      selection.length
        ? data.filter((item) => selection.includes(item.id))
        : [],
    );
  };
  const location = (useLocation().pathname ?? "").split("/");

  return (
    <>
      <div
        className={`flex max-md:flex-col scroll-m-3 lg:overflow-hidden overflow-auto gap-4 justify-between items-center ${
          pathname == "students" ? "py-0" : "py-10"
        }   w-full px-[33px]  ${
          isOrientation ? "border-b border-[#A7A5A6]/20" : ""
        }  pb-[16px] ${props.className} `}
      >
        {children ? (
          children
        ) : (
          <div className="flex gap-3 items-center">
            {
              // [].includes(location[1])
              !["response", "certificate", "advertisment"].includes(
                location[1],
              ) && (
                <div>
                  {location.length > 2 && (
                    <ChevronLeft
                      className="cursor-pointer"
                      onClick={() => {
                        navigate(`/${location[1]}`);
                      }}
                    />
                  )}
                </div>
              )
            }
            <ul className="list-none">
              {(type || pathname || title) && (
                <li className="text-[clamp(20px,6vw,32px)] leading-[40px] font-bold capitalize">
                  {title || (type || pathname)?.replace(/-/g, " ")}
                </li>
              )}
              {description && (
                <li className="text-[#7B7679] text-[14px]">{description}</li>
              )}
            </ul>
          </div>
        )}

        <div
          className={`flex flex-wrap gap-3  items-center transition-all ease-in-out ${
            openTypes.includes(pageMode || "") && isOrientation
              ? "delay-100  translate-x-[100px]"
              : openTypes.includes(modalMode || "")
                ? "delay-100  translate-x-[150px]"
                : "translate-x-0"
          }`}
        >
          {bulkActions && selectedRows?.length > 0 && (
            <div
              className={cn([
                "flex items-center gap-2 transition-all ease-in-out",
                orientationMode === "drag" && "translate-x-[90px]",
              ])}
            >
              {searchParams.get("orientation") == "grid" && (
                <>
                  {" "}
                  <Checkbox
                    id="check-1"
                    className="cursor-pointer"
                    checked={
                      data.length > 0 &&
                      data.every((item) => selectedRows.includes(item.id))
                    }
                    onCheckedChange={() => handleCheck("all")}
                  />
                  <label
                    htmlFor="check-1"
                    className="cursor-pointer text-[14px]"
                  >
                    Select All ({selectedRows.length})
                  </label>
                </>
              )}

              {inject_bulk_actions}

              <Button
                variant={"outline"}
                onClick={() => {
                  setSelectedRows([]);
                }}
              >
                Clear
              </Button>
              <Button
                variant={"destructive"}
                onClick={() => {
                  if (pathname === "blogs") {
                    if (searchParams.get("data-category") === "Deleted-4") {
                      handleAction({
                        type: "confirm",
                        data: { type: "bulk" },
                      });
                    } else {
                      handleAction({
                        type: "confirm",
                        data: { type: "bulk-edit" },
                      });
                    }
                  } else {
                    handleAction({
                      type: "confirm",
                      data: { type: "bulk" },
                    });
                  }
                }}
              >
                Delete {`(${selectedRows.length})`}
              </Button>
            </div>
          )}

          <>
            {inject_ui_table_actions && !openTypes.includes(pageMode || "") && (
              <div className="flex flex-wrap space-x-2 gap-1 transition-all ease-in-out justify-start">
                <>
                  {count > 0 && !hideSort && (
                    <div
                      className={`flex flex-wrap gap-3 transition-all ease-in-out ${
                        ["drag"].includes(orientationMode || "")
                          ? "delay-100  translate-x-[100px] z-[-1] bg-white opacity-1 "
                          : ["drag"].includes(modalMode || "")
                            ? "delay-100  translate-x-[150px] opacity-0"
                            : "translate-x-0"
                      }`}
                    >
                      <Popover>
                        <PopoverTrigger>
                          <Button
                            variant={
                              searchParams.get("ordering")
                                ? "destructive"
                                : "ghost"
                            }
                            className="border shadow-2xs"
                          >
                            {searchParams.get("ordering")
                              ? "Sort Active"
                              : "Sort"}
                            <ArrowUpDownIcon />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="!p-0 !mt-4 w-[250px]">
                          <SortingForm />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </>

                {inject_ui_table_actions}
              </div>
            )}
          </>

          {isGroupAction && (
            <>
              {!isFormPending && openTypes.includes(pageMode || "") && (
                <div className="flex gap-2">
                  {[
                    "courses",
                    "masterclass-courses",
                    "jrp-courses",
                  ].includes(pathname) && (
                    <Button
                      type="button"
                      disabled={!searchParams.get("page-item")}
                      onClick={() => {
                        searchParams.set(
                          "configure-course",
                          searchParams.get("page-item") || ""
                        );
                        setSearchParams(searchParams);
                      }}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                    >
                      Configure Course Sections (Tabs)
                    </Button>
                  )}
                  <Button
                    variant={"outline"}
                    className="capitalize"
                    onClick={() => handleAction({ type: "page-close" })}
                  >
                    Cancel
                  </Button>
                </div>
              )}

              {type == "giveaway" && (
                <Link to="manage-source">
                  <Button>Manage Source</Button>
                </Link>
              )}

              {[
                "request-form",
                "contact-form",
                "enrollment",
                "masterclass-enrollment",
                "giveaway",
                "festival",
                "masterclass-invoice",
                "jrp-invoice",
                "session-inquiry",
                "activity-log",
                "invoice",
              ].includes(type as string) ||
              ([
                "invoice",
                "masterclass-invoice",
                "jrp-invoice",
                "activity-log",
                "cricket-predictions",
                "cricket-userdetails",
                "cricket-match-winners",
              ].includes(pathname) &&
                ["add", ""].includes(pageMode || ""))
                ? null
                : selectedRows?.length == 0 && (
                    <Button
                      className={cn("capitalize", className)}
                      form={formId}
                      onClick={() => {
                        ["blogs", "blogsC"].includes(pathname)
                          ? navigate(`/add-${pathname}`)
                          : (pageMode || modalMode) !== "edit" &&
                            handleAction({
                              type: pageViewList.includes(
                                type || pathname || title,
                              )
                                ? "page-add"
                                : "modal-add",
                            });
                      }}
                      disabled={isFormPending}
                    >
                      {isFormPending ? (
                        "processing..."
                      ) : (
                        <>
                          {["invoice"].includes(pathname)
                            ? "Send "
                            : pageMode === "edit"
                              ? "Edit "
                              : "Add"}{" "}
                          {/*Any pages Add button is overhere*/}
                          {(type || pathname)?.replace(/-/g, " ") || title}
                        </>
                      )}
                    </Button>
                  )}
            </>
          )}
          {isOrientation && grid && (
            <div
              className={`flex items-center gap-1 transition-all ease-in-out ${
                openTypes.includes(pageMode || modalMode || "")
                  ? "translate-x-[100px] scale-0 opacity-0"
                  : "opacity-100 scale-100 translate-x-0 delay-50"
              }`}
            >
              <RowBar
                onClick={() => handleGroup("row")}
                className={`cursor-pointer`}
                color={getOrientation() === "row" ? "#157254" : ""}
              />
              <GridBar
                onClick={() => handleGroup("grid")}
                className={`cursor-pointer`}
                color={getOrientation() === "grid" ? "#157254" : ""}
              />
              <DragBar
                onClick={() => handleGroup("drag")}
                className={`cursor-pointer h-[20px] w-[20px]`}
                color={getOrientation() === "drag" ? "#157254" : ""}
              />
            </div>
          )}
        </div>

        {/* Buttons section is ending here */}
      </div>
    </>
  );
};

type TableProps = { columns: any[]; rows: any[]; [key: string]: any };
const Table: React.FC<TableProps> = ({ columns = [], rows = [], ...props }) => {
  return (
    <>
      <DataTable
        style={{ width: "100%" }}
        columns={columns}
        data={rows}
        dense
        {...props}
      />
    </>
  );
};

const SortableItem = ({
  data,
  disabled = false,
  checked = [],
  handleCheck,
  index,
  offset, //for id to be continued in the pagination
  columns = [],
}: {
  data: any;
  disabled: boolean;
  checked: number[];
  index: number;
  offset?: number; //for id to be continued in the pagination
  handleCheck: (type: "single" | "all", id?: number) => void;
  columns: any[];
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: data.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: disabled ? "default" : "grab",
  };
  const pathname = usePathname();
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-gray-50 border border-[#F4F4FF] h-[100px] max-md:h-[80px] max-sm:h-[60px] flex justify-between items-center"
    >
      {/* Static ID / Checkbox column */}
      <td className="w-[80px] ">
        <div className="flex gap-2 ps-[10px]">
          {!disabled && (
            <span
              {...attributes}
              {...listeners}
              className="cursor-grab text-gray-400"
            >
              <Equal />
            </span>
          )}
          {!["students"].includes(pathname) && (
            <Checkbox
              checked={checked.includes(data.id)}
              onCheckedChange={() => handleCheck("single", data.id)}
            />
          )}

          {data.id || (offset || 0) + index + 1}
        </div>
      </td>

      {/* Dynamic columns */}
      {columns
        ?.filter((item) => item.checked || item.checked === undefined)
        .slice(1)
        .map((col, index) => (
          <td
            key={index}
            className="pr-3"
            style={{ minWidth: col?.width ?? 200 }}
          >
            <div className="flex">
              {col.cell
                ? col.cell(data, index)
                : col.selector
                  ? col.selector(data)
                  : null}
            </div>
          </td>
        ))}
    </tr>
  );
};

const SortableTable = ({
  rows = [],
  columns = [],
  endpoint = "",
  sortableFilter = {},
  refetch,
}: {
  rows: any[];
  columns: any[];
  endpoint?: string;
  refetch?: () => void;
  sortableFilter?: object;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  /* integration */

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteScroller({
      endpoint,
      filter: sortableFilter,
      enabled:
        ["drag"].includes(searchParams.get("orientation") || " ") && endpoint
          ? true
          : false,
    });

  const allScrolledData = useMemo(() => {
    return data?.pages.flatMap((page: any) => page.results) || [];
  }, [data]);

  const { ref, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const [items, setItems] = useState(
    ["drag"].includes(searchParams.get("orientation") || " ")
      ? (allScrolledData ?? [])
      : (rows ?? []),
  );

  useEffect(() => {
    if (searchParams.get("orientation") !== "drag") {
      const isSame =
        rows.length === items.length &&
        rows.every(
          (row, i) =>
            row.id === items[i]?.id &&
            row.email_sent_at === items[i]?.email_sent_at
        );
      if (!isSame) {
        setItems(() => rows);
      }
      return;
    }

    if (allScrolledData.length === items.length) {
      const isSame = allScrolledData.every(
        (item, i) =>
          item.id === items[i].id &&
          item.email_sent_at === items[i].email_sent_at,
      );
      if (!isSame) {
        return; // black return change !x!!!
      }
      return setItems(() => allScrolledData);
    }

    return setItems(() => allScrolledData);
  }, [allScrolledData, searchParams, rows]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const queryClient = useQueryClient();
  const pathname = usePathname();

  const handleDragApi = async ({ model = "", target = "", goal = "" }: any) => {
    let response = await fetch(
      baseURL +
        apiEndpoint["drag"] +
        `?model=${model}&target=${target}&goal=${goal}`,
      getOptions(),
    );
    if (response.ok) {
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing`],
      });
      !!refetch && refetch();
      return;
    }
    showToast({
      description: "failed to reorder",
      title: "failed",
      type: "error",
    });
    throw new Error("Something went wrong!");
  };

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });

      const findTragetPos = items.find((items) => items.id === active.id)?.[
        "id"
      ];
      let findGoalPos = items.find((items) => items.id === over.id)?.["order"];

      if (findGoalPos === undefined) {
        console.log("I WAS HERE BECAUSE FINDGOAL WAS UNDEFINED");
        findGoalPos =
          items.find((item) => item.id === over.id)?.["position"] ||
          items.find((item) => item.id === over.id)?.["sort_order"] ||
          items.find((item) => item.id === over.id)?.["sequence"];
      }

      console.log(
        "Active item:",
        items.find((item) => item.id === active.id),
      );
      console.log(
        "Over item:",
        items.find((item) => item.id === over.id),
      );

      handleDragApi({
        model: bulkType[`${endpoint}`],
        target: findTragetPos,
        goal: findGoalPos,
      });
    }
  }

  const { selectedRows, setSelectedRows, setSelectedRowDetails } =
    useSelectionStore();
  const handleCheck = (type: "single" | "all", id?: string | number) => {
    let selection: number[] = [];

    if (type === "all") {
      const currentPageIds = items.map((item) => item.id);

      // Are all current page items already selected?
      const allSelectedForPage = currentPageIds.every((pid) =>
        selectedRows.includes(pid),
      );

      selection = allSelectedForPage
        ? // Deselect only the current page items
          selectedRows.filter((id) => !currentPageIds.includes(id))
        : // Add only missing current page items
          [
            ...selectedRows,
            ...currentPageIds.filter((pid) => !selectedRows.includes(pid)),
          ];

      setSelectedRows(selection);
      setSelectedRowDetails(
        selection.length
          ? items.filter((item) => selection.includes(item.id))
          : [],
      );
      return;
    }

    if (typeof id === "undefined") return;
    // @ts-ignore
    selection = selectedRows.includes(Number(id))
      ? selectedRows.filter((i) => i !== Number(id))
      : [...selectedRows, Number(id)];
    console.log(
      "this is the selection -- inside handleCheck",
      selection,
      items,
    );
    setSelectedRows(selection);
    setSelectedRowDetails(
      selection.length
        ? items.filter((item) => selection.includes(item.id))
        : [],
    );
  };

  const getSorted = (col: any) => {
    const name = col.accessKey?.toLowerCase();
    const current = searchParams.get("ordering");

    if (current === name) {
      // Already ascending → change to descending
      searchParams.set("ordering", `-${name}`);
    } else if (current === `-${name}`) {
      // Already descending → reset
      searchParams.delete("ordering");
    } else {
      // Not sorted → set to ascending
      searchParams.set("ordering", name);
    }

    setSearchParams(searchParams);
  };

  return (
    <div className="w-full px-[20px] py-4 overflow-x-auto">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <table className="min-w-full table-auto border-collapse text-left overflow-hidden">
          <thead
            className="text-[#1DA077] border font-semibold leading-[16px] bg-[#E8F6F1] h-[61px] border-b"
            style={{
              boxShadow:
                "0px 1px 2px 0px rgba(16, 24, 40, 0.04), 0px 1px 2px 0px rgba(16, 24, 40, 0.04)",
            }}
          >
            <tr className="flex justify-between items-center">
              {/* Static Checkbox/ID column */}
              <th className="w-[80px] text-nowrap flex ps-[10px] items-center font-fustat text-sm font-semibold text-gray-700">
                <div className="flex text-[#1DA077] items-center justify-center gap-2">
                  {!["students"].includes(pathname) && (
                    <Checkbox
                      checked={
                        items.length > 0 &&
                        items.every((item) => selectedRows.includes(item.id))
                      }
                      onCheckedChange={() => handleCheck("all")}
                    />
                  )}
                  <div className="flex text-[#1DA077]  h-[80px] items-center gap-2 mx-2 ">
                    ID
                  </div>
                </div>
              </th>

              {/* Dynamic columns */}
              {columns
                ?.filter((item) => item.checked || item.checked === undefined)
                .slice(1)
                .map((col, index) => {
                  const getActivity = (col: any): "up" | "down" | null => {
                    const current = searchParams.get("ordering");
                    const key = col?.accessKey?.toLowerCase();
                    if (current === key) return "up";
                    if (current === `-${key}`) return "down";
                    return null;
                  };
                  const activity = getActivity(col);
                  return (
                    <th
                      key={index}
                      style={{ minWidth: col?.width ?? 200 }}
                      className={`h-[80px] select-none text-nowrap text-sm font-semibold text-gray-700 group`}
                    >
                      <div className="flex text-[#1DA077]  h-[80px] items-center gap-2 ">
                        {col.name}{" "}
                        {col.sortable && col.accessKey && (
                          <>
                            <div className="cursor-pointer">
                              <ChevronsIcon
                                onClick={() => getSorted(col)}
                                type={activity}
                              />
                            </div>

                            <Popover>
                              <PopoverTrigger>
                                <EllipsisVertical
                                  size={16}
                                  className={cn(["cursor-pointer"])}
                                />
                              </PopoverTrigger>
                              <PopoverContent>
                                <ul className="space-y-2">
                                  <li
                                    className="cursor-pointer"
                                    onClick={() => {
                                      if (activity === "up") {
                                        searchParams.delete("ordering");
                                        setSearchParams(searchParams);
                                      } else {
                                        searchParams.set(
                                          "ordering",
                                          col.accessKey.toLowerCase(),
                                        );
                                        setSearchParams(searchParams);
                                      }
                                    }}
                                  >
                                    {activity === "up" ? (
                                      <div className="text-red-500 flex gap-[8px] cursor-pointer">
                                        <ArrowUpDown size={16} /> Reset
                                        Ascending Sort
                                      </div>
                                    ) : (
                                      <div className="flex gap-[6px] cursor-pointer">
                                        <ArrowUpNarrowWide size={16} /> Sort
                                        Ascending
                                      </div>
                                    )}
                                  </li>

                                  <li
                                    onClick={() => {
                                      if (activity === "down") {
                                        searchParams.delete("ordering");
                                        setSearchParams(searchParams);
                                      } else {
                                        searchParams.set(
                                          "ordering",
                                          `-${col.accessKey.toLowerCase()}`,
                                        );
                                        setSearchParams(searchParams);
                                      }
                                    }}
                                  >
                                    {activity === "down" ? (
                                      <div className="text-red-500  cursor-pointer flex gap-[6px]">
                                        <ArrowUpDown size={16} /> Reset
                                        Descending Sort
                                      </div>
                                    ) : (
                                      <div className="flex gap-[6px] cursor-pointer">
                                        <ArrowDownWideNarrow size={16} /> Sort
                                        Descending
                                      </div>
                                    )}
                                  </li>
                                </ul>
                              </PopoverContent>
                            </Popover>
                          </>
                        )}
                      </div>
                    </th>
                  );
                })}
            </tr>
          </thead>

          <tbody>
            <SortableContext
              items={items?.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {(() => {
                const isDrag = searchParams.get("orientation") === "drag";
                const tpage = isDrag
                  ? 1
                  : Number(searchParams.get("tpage")) || 1;
                const per = Number(searchParams.get("per")) || 10;
                const offset = (tpage - 1) * per;

                return items?.map((item, index) => (
                  <SortableItem
                    index={index}
                    offset={offset}
                    key={item.id}
                    data={item}
                    columns={columns}
                    checked={selectedRows as number[]}
                    handleCheck={handleCheck}
                    disabled={
                      !["drag"].includes(
                        searchParams.get("orientation") || "",
                      ) || false
                    }
                  />
                ));
              })()}
            </SortableContext>

            {/* integration of scroller fetcher */}
            {["drag"].includes(searchParams.get("orientation") || "") && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <div
                    ref={ref}
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    {isFetchingNextPage ? (
                      <>
                        {" "}
                        <SkeletonOne repeat={2} />
                      </>
                    ) : hasNextPage ? (
                      <SkeletonOne repeat={2} />
                    ) : //@ts-ignore
                    data?.pages[0]?.count === items.length ? (
                      <p>You have reached end~</p>
                    ) : (
                      <SkeletonOne repeat={2} className={"mt-[80px]"} />
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </DndContext>
    </div>
  );
};

export { SortableTable, Table, TableHeader };
